import isCalendarEventFeedbackAnswerValid from './isCalendarEventFeedbackAnswerValid';

import type { CalendarEventFeedbackQuestion } from 'shared-types';

type SubmittedAnswer = { answer: unknown; questionId: number };

type Params = {
  answers: SubmittedAnswer[];
  questions: CalendarEventFeedbackQuestion[];
};

type CalendarEventFeedbackAnswersError =
  | 'answer_missing'
  | 'duplicate_answer'
  | 'invalid_answer'
  | 'unknown_question';

/**
 * Re-validates a whole feedback form submission against its questions and
 * returns which rule it broke, or undefined when it is valid: every submitted
 * answer has to reference a question on this event's form, no question may be
 * answered more than once, every required question has to have a valid answer,
 * and optional ones may be left out entirely.
 */
const validateCalendarEventFeedbackAnswers = ({
  answers,
  questions
}: Params): CalendarEventFeedbackAnswersError | undefined => {
  const questionIds = new Set(questions.map(({ id }) => id));
  if (!answers.every(({ questionId }) => questionIds.has(questionId))) {
    return 'unknown_question';
  }

  const submittedQuestionIds = answers.map(({ questionId }) => questionId);
  if (new Set(submittedQuestionIds).size !== submittedQuestionIds.length) {
    return 'duplicate_answer';
  }

  const answersByQuestionId = new Map(
    answers.map((answer) => [answer.questionId, answer])
  );

  const invalidQuestion = questions.find((question) => {
    const submitted = answersByQuestionId.get(question.id);
    if (!submitted) return question.isRequired;

    return !isCalendarEventFeedbackAnswerValid({
      answer: submitted.answer,
      question
    });
  });
  if (!invalidQuestion) return undefined;

  return answersByQuestionId.has(invalidQuestion.id)
    ? 'invalid_answer'
    : 'answer_missing';
};

export default validateCalendarEventFeedbackAnswers;
export type { CalendarEventFeedbackAnswersError };
